import BaseChart from './BaseChart';
import { CHART_ANIMATIONS } from '../config/animations';

/**
 * Pie chart implementation using VChart
 */
class PieChart extends BaseChart {
  /**
   * Get chart specification for pie chart
   * @returns {Object} Chart specification
   */
  getChartSpecification() {
    const commonConfig = this.getCommonConfig();
    const data = this.prepareData();

    // Determine if we should create a donut chart
    const isDonut = this.options.innerRadius > 0;

    // Determine fields for name and value
    const nameField = this._resolveNameField();
    const valueField = this._resolveValueField();
    
    // Handle color configuration
    let colorConfig = {};
    if (this.options.color) {
      // If colors are provided, use them
      colorConfig = {
        seriesField: nameField,
        color: Array.isArray(this.options.color) ? this.options.color : [this.options.color]
      };
    }
    
    return {
      type: 'pie',
      data,
      valueField,
      categoryField: nameField,
      ...colorConfig,
      
      // Radius configuration - MUST specify outerRadius
      outerRadius: this.options.outerRadius || 0.8,
      innerRadius: isDonut ? (this.options.innerRadius || 0.6) : 0,
      
      // Pie specific styling
      pie: {
        style: {
          stroke: '#fff',
          lineWidth: 2,
          ...this.options.pieStyle
        },
        state: {
          hover: {
            offset: 10,
            stroke: '#fff',
            lineWidth: 2
          },
          ...this.options.pieState
        }
      },
      
      // Pie label configuration
      label: {
        visible: this.options.showLabels !== false,
        position: this.options.labelPosition || 'outside',
        style: {
          fontSize: 12,
          fontWeight: 'normal',
          ...this.options.labelStyle
        },
        // Use function-based formatter to correctly calculate percentages
        formatMethod: (text, datum) => {
          if (!datum) return '';
          
          const name = datum[nameField];
          const value = Number(datum[valueField] || 0);
          const total = datum._pieTotalValue || 1;
          const percentage = ((value / total) * 100).toFixed(1);
          
          return `${name}: ${percentage}%`;
        }
      },
      
      // Legend configuration
      legends: {
        visible: this.options.legends?.visible !== false,
        position: this.options.legends?.position || 'right',
        ...this.options.legends
      },
      
      // Animation configuration specific to pie charts
      animationAppear: {
        ...CHART_ANIMATIONS.pie.appear,
        ...this.options.animationAppear
      },
      
      // Common configuration from BaseChart
      ...commonConfig
    };
  }
  
  /**
   * Prepare data for VChart format with total calculation
   * @returns {Array} Formatted data for VChart
   */
  prepareData() {
    // Ensure data is in the correct format for VChart
    if (!Array.isArray(this.data)) {
      // Convert non-array data to array format
      this.data = [this.data];
    }

    const valueField = this._resolveValueField();

    // Calculate total for percentage
    let total = 0;
    this.data.forEach(item => {
      // Ensure numeric values
      const value = Number(item[valueField] || 0);
      total += value;
    });
    
    // Add total to data for percentage calculation
    const formattedData = this.data.map(item => ({
      ...item,
      _pieTotalValue: total
    }));
    
    const result = [
      {
        id: 'pieData',
        values: formattedData
      }
    ];
    
    return result;
  }
}

export default PieChart; 